import React, { useState, useEffect } from 'react';
import { X, Loader, CheckCircle, Pencil, Trash2, Check } from 'lucide-react';
import { motion } from 'framer-motion';
import api from '../../../api/apiClient';
import workshop3Service from '../../../services/workshop3Service'; // Use hydration service
import ecosystemMeasuresService from '../../../services/ecosystemMeasuresService';

const InlineAiSuggestions = ({ isVisible, onClose, onGenerate, analysisId, existingMeasures = [] }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [stakeholders, setStakeholders] = useState([]);
  const [attackPaths, setAttackPaths] = useState([]);
  const [selectedStakeholders, setSelectedStakeholders] = useState([]);
  const [selectedAttackPaths, setSelectedAttackPaths] = useState([]);
  const [generationFocus, setGenerationFocus] = useState('balanced');
  const [measuresPerPath, setMeasuresPerPath] = useState(1); // Default to 1 measure per attack path
  const [generatedMeasures, setGeneratedMeasures] = useState([]);
  const [allGeneratedMeasures, setAllGeneratedMeasures] = useState([]);
  const [error, setError] = useState(null);
  const [editingIndex, setEditingIndex] = useState(null);
  const [editDraft, setEditDraft] = useState({ title: '', category: '', priority: '' });

  // Options canoniques (alignées sur MeasureForm.jsx : mêmes ids)
  const CATEGORY_OPTIONS = [
    { id: 'governance', label: 'Gouvernance' },
    { id: 'protection', label: 'Protection' },
    { id: 'defense', label: 'Défense' },
    { id: 'resilience', label: 'Résilience' },
    { id: 'other', label: 'Autre' }
  ];
  const PRIORITY_OPTIONS = [
    { id: 'high', label: 'Haute' },
    { id: 'medium', label: 'Moyenne' },
    { id: 'low', label: 'Basse' }
  ];

  // Ids des chemins déjà couverts par les lots accumulés + lot courant
  // Association réutilisant la logique de MeasureTable.jsx:getAssociatedAttackPaths
  // (Méthode 1 : tableau attackPaths avec comparaison floue, Méthode 2 : champ
  // attackPathId, repli : attackPathsDetails).
  const normalizeAttackPathId = (id) => {
    if (!id) return '';
    const parts = String(id).split('-');
    if (parts.length > 1) {
      const lastPart = parts[parts.length - 1];
      if (lastPart.length >= 13 && /^\d+$/.test(lastPart)) {
        return parts.slice(0, -1).join('-');
      }
    }
    return String(id);
  };

  const attackPathIdsMatch = (id1, id2) => {
    if (!id1 || !id2) return false;
    if (id1 === id2) return true;
    const normalized1 = normalizeAttackPathId(id1);
    const normalized2 = normalizeAttackPathId(id2);
    return normalized1 === normalized2 && normalized1.length > 0;
  };

  const isMeasureLinkedToPath = (measure, pathId) => {
    if (!measure || !pathId) return false;
    // Méthode 1 : tableau attackPaths (comparaison floue)
    if (Array.isArray(measure.attackPaths) && measure.attackPaths.length > 0) {
      if (measure.attackPaths.some((mpId) => attackPathIdsMatch(mpId, pathId))) return true;
    }
    // Méthode 2 : champ attackPathId (mesures générées par l'IA)
    if (measure.attackPathId && attackPathIdsMatch(measure.attackPathId, pathId)) return true;
    // Repli : attackPathsDetails (objets hydratés ou ids bruts)
    if (Array.isArray(measure.attackPathsDetails) && measure.attackPathsDetails.length > 0) {
      return measure.attackPathsDetails.some((d) => {
        const detailId = typeof d === 'string' ? d : (d && (d.id || d._id));
        return detailId ? attackPathIdsMatch(detailId, pathId) : false;
      });
    }
    return false;
  };

  const measureCountsByPath = React.useMemo(() => {
    const counts = {};
    attackPaths.forEach((p) => {
      const saved = Array.isArray(existingMeasures)
        ? existingMeasures.filter((m) => isMeasureLinkedToPath(m, p.id)).length
        : 0;
      const fresh = [...allGeneratedMeasures, ...generatedMeasures]
        .filter((m) => isMeasureLinkedToPath(m, p.id)).length;
      counts[p.id] = { saved, fresh, total: saved + fresh };
    });
    return counts;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attackPaths, existingMeasures, allGeneratedMeasures, generatedMeasures]);

  const getCountBadge = (pathId) => {
    const c = measureCountsByPath[pathId] || { saved: 0, fresh: 0, total: 0 };
    if (c.total === 0) {
      return {
        label: '0 mesure — non couvert',
        className: 'bg-slate-100 text-slate-500 border-slate-200',
        title: 'Aucune mesure pour ce chemin — non couvert'
      };
    }
    const parts = [];
    if (c.saved > 0) parts.push(`${c.saved} existante${c.saved > 1 ? 's' : ''}`);
    if (c.fresh > 0) parts.push(`${c.fresh} nouvelle${c.fresh > 1 ? 's' : ''}`);
    const label = c.saved > 0 && c.fresh > 0
      ? parts.join(' + ')
      : c.total === 1 && c.saved === 1 ? '1 mesure' : `${c.total} mesures`;
    return {
      label,
      className: 'bg-indigo-50 text-indigo-700 border-indigo-200',
      title: parts.length > 0 ? `${c.total} mesure${c.total > 1 ? 's' : ''} (${parts.join(' + ')})` : `${c.total} mesures`
    };
  };

  const coveredPathIds = React.useMemo(() => {
    const all = [...allGeneratedMeasures, ...generatedMeasures];
    const ids = new Set();
    all.forEach((m) => {
      (m.attackPaths || []).forEach((id) => ids.add(id));
      // Fallback : certaines mesures n'ont que sourceRisks — on ne peut pas
      // en déduire l'id ici, la correspondance se fait dans applyMeasures.
    });
    return ids;
  }, [allGeneratedMeasures, generatedMeasures]);

  const totalMeasures = [...allGeneratedMeasures, ...generatedMeasures];
  const remainingPaths = attackPaths.filter((p) => !coveredPathIds.has(p.id));

  // Load stakeholders and attack paths
  useEffect(() => {
    if (analysisId) {
      loadData();
    }
  }, [analysisId]);

  const loadData = async () => {
    setIsLoading(true);
    setError(null);
    try {
      // Load stakeholders - use the correct endpoint with atelier3 prefix
      const stakeholdersResponse = await api.get(`/analyses/${analysisId}/atelier3/stakeholders`);
      if (stakeholdersResponse.success && stakeholdersResponse.data) {
        setStakeholders(stakeholdersResponse.data);
      }

      // Load hydrated attack paths from Workshop 3 — only retained (like W4)
      console.log('🔄 Loading hydrated attack paths for AI generation (retained only)...');
      const pathsResponse = await workshop3Service.getAttackPaths(analysisId, true);
      if (pathsResponse.success && pathsResponse.data) {
        const retainedPaths = Array.isArray(pathsResponse.data)
          ? pathsResponse.data.filter((p) => p.retained !== false)
          : [];
        console.log('✅ Loaded', retainedPaths.length, 'retained attack paths');
        setAttackPaths(retainedPaths);
      }
    } catch (error) {
      console.error('Error loading data for AI generation:', error);
      setError('Erreur lors du chargement des données. Veuillez réessayer.');
    } finally {
      setIsLoading(false);
    }
  };

  // Generate measures using AI
  const generateMeasures = async () => {
    setIsGenerating(true);
    setError(null);
    try {
      // Use the ecosystem measures service
      const response = await ecosystemMeasuresService.generateMeasures({
        analysisId,
        attackPaths: selectedAttackPaths,
        focus: generationFocus,
        measuresPerPath: measuresPerPath // Add the number of measures per attack path
      });

      if (response.success && response.data) {
        // Déduplique le nouveau lot contre les mesures déjà accumulées (par titre)
        const seen = new Set(
          [...allGeneratedMeasures, ...generatedMeasures]
            .map((m) => (m.title || '').trim().toLowerCase())
            .filter(Boolean)
        );
        const freshBatch = (Array.isArray(response.data) ? response.data : []).filter((m) => {
          const key = (m.title || '').trim().toLowerCase();
          if (key && seen.has(key)) return false;
          if (key) seen.add(key);
          return true;
        });
        // Auto-accumulation : conserve le lot précédent avant d'afficher le nouveau
        if (generatedMeasures.length > 0) {
          const prevTitles = new Set(
            allGeneratedMeasures.map((m) => (m.title || '').trim().toLowerCase()).filter(Boolean)
          );
          const toAccumulate = generatedMeasures.filter((m) => {
            const key = (m.title || '').trim().toLowerCase();
            if (key && prevTitles.has(key)) return false;
            if (key) prevTitles.add(key);
            return true;
          });
          if (toAccumulate.length > 0) {
            setAllGeneratedMeasures((prev) => [...prev, ...toAccumulate]);
          }
        }
        setGeneratedMeasures(freshBatch);
        setEditingIndex(null);
      } else {
        throw new Error(response.message || 'Erreur lors de la génération');
      }
    } catch (error) {
      console.error('Error generating measures:', error);
      setError(`Erreur: ${error.message || 'Problème de génération avec l\'Assistant ACG'}`);
    } finally {
      setIsGenerating(false);
    }
  };

  // Apply generated measures (lots accumulés + lot courant)
  const applyMeasures = () => {
    const combined = [...allGeneratedMeasures, ...generatedMeasures];
    // Make sure each measure has the attackPaths property
    const measuresToApply = combined.map(measure => {
      // If attackPaths is missing or empty but we have sourceRisks, try to match them
      if ((!measure.attackPaths || measure.attackPaths.length === 0) &&
        measure.sourceRisks && measure.sourceRisks.length > 0) {

        // Find matching attack paths based on sourceRisks
        const matchingPaths = attackPaths.filter(path =>
          measure.sourceRisks.some(source =>
            (path.sourceRiskName && path.sourceRiskName.includes(source)) ||
            (path.sourceRiskDescription && path.sourceRiskDescription.includes(source)) ||
            (source && path.sourceRiskName && source.includes(path.sourceRiskName)) ||
            (source && path.sourceRiskDescription && source.includes(path.sourceRiskDescription))
          )
        );

        return {
          ...measure,
          attackPaths: matchingPaths.map(path => path.id)
        };
      }

      return measure;
    });

    onGenerate(measuresToApply);
  };

  // Raccourci : sélectionner uniquement les chemins non encore couverts
  const selectRemainingPaths = () => {
    setSelectedAttackPaths(remainingPaths.map((p) => p.id));
  };

  // Édition / suppression par ligne (écrit dans la bonne liste source)
  const startEdit = (index) => {
    const measure = totalMeasures[index];
    if (!measure) return;
    setEditingIndex(index);
    setEditDraft({
      title: measure.title || '',
      category: measure.category || '',
      priority: measure.priority || ''
    });
  };

  const cancelEdit = () => {
    setEditingIndex(null);
    setEditDraft({ title: '', category: '', priority: '' });
  };

  const saveEdit = (index) => {
    const boundary = allGeneratedMeasures.length;
    if (index < boundary) {
      setAllGeneratedMeasures((prev) => {
        const next = [...prev];
        next[index] = { ...next[index], ...editDraft };
        return next;
      });
    } else {
      const currentIdx = index - boundary;
      setGeneratedMeasures((prev) => {
        const next = [...prev];
        next[currentIdx] = { ...next[currentIdx], ...editDraft };
        return next;
      });
    }
    cancelEdit();
  };

  const deleteMeasure = (index) => {
    const boundary = allGeneratedMeasures.length;
    if (index < boundary) {
      setAllGeneratedMeasures((prev) => prev.filter((_, i) => i !== index));
    } else {
      const currentIdx = index - boundary;
      setGeneratedMeasures((prev) => prev.filter((_, i) => i !== currentIdx));
    }
    if (editingIndex === index) cancelEdit();
  };

  const toggleAttackPath = (pathId) => {
    setSelectedAttackPaths((prev) =>
      prev.includes(pathId) ? prev.filter((id) => id !== pathId) : [...prev, pathId]
    );
  };

  if (!isVisible) return null;

  return (
    <motion.div
      initial={{ opacity: 0, height: 0, y: -20 }}
      animate={{ opacity: 1, height: 'auto', y: 0 }}
      exit={{ opacity: 0, height: 0, y: -20 }}
      transition={{ duration: 0.5, ease: [0.4, 0, 0.2, 1] }}
      className="mb-6 overflow-hidden"
      tabIndex={-1}
    >
      <div className="relative bg-gradient-to-br from-purple-500 via-indigo-500 to-pink-500 p-[1px] rounded-2xl shadow-2xl">
        <div className="bg-white rounded-2xl backdrop-blur-sm">
          {/* Animated Header */}
          <motion.div
            initial={{ x: -20, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ delay: 0.1 }}
            className="flex items-center justify-between p-5 border-b border-slate-100"
          >
            <div>
              <h3 className="text-lg font-bold bg-gradient-to-r from-purple-600 to-indigo-600 bg-clip-text text-transparent">
                Assistant ACG AI
              </h3>
              <p className="text-xs text-slate-500 font-medium">
                Génération de mesures de sécurité intelligentes
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-xl transition-all duration-200 hover:scale-110"
            >
              <X size={20} />
            </button>
          </motion.div>

          {/* Content Area */}
          <div className="p-5 max-h-96 overflow-y-auto">
            {error && (
              <div className="mb-4 bg-red-50 border-l-4 border-red-500 p-4 rounded-md">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

            {isLoading ? (
              <div className="flex justify-center items-center h-40">
                <Loader className="animate-spin text-blue-500" size={24} />
                <span className="ml-2 text-gray-600">Chargement des données...</span>
              </div>
            ) : isGenerating ? (
              <div className="flex flex-col justify-center items-center h-40">
                <Loader className="animate-spin text-blue-500" size={24} />
                <span className="mt-2 text-gray-600">Génération des mesures en cours...</span>
                <span className="mt-1 text-xs text-gray-500">Cela peut prendre quelques instants</span>
              </div>
            ) : totalMeasures.length === 0 ? (
              <div className="space-y-6">
                {/* Instruction */}
                <div className="mb-6">
                  <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 p-6 rounded-lg">
                    <div className="flex items-start">
                      <div className="flex-shrink-0">
                        <svg className="h-6 w-6 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <h4 className="text-sm font-semibold text-blue-900 mb-1">Comment ça fonctionne</h4>
                        <p className="text-sm text-blue-700">
                          Sélectionnez les chemins d'attaque pour lesquels vous souhaitez générer des mesures de sécurité.
                          L'Assistant ACG créera des mesures spécifiques pour traiter les sources de risque et protéger les objectifs visés.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Attack path selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Chemins d'attaque
                  </label>
                  <div className="max-h-60 overflow-y-auto border rounded-md p-3 bg-gray-50">
                    {attackPaths.length === 0 ? (
                      <p className="text-sm text-gray-500 p-2">Aucun chemin d'attaque disponible</p>
                    ) : (
                      <div className="space-y-3">
                        {attackPaths.map(path => (
                          <div key={path.id} className="p-3 border rounded-md bg-white hover:bg-blue-50 transition-colors">
                            <div className="flex items-start">
                              <input
                                type="checkbox"
                                id={`path-${path.id}`}
                                checked={selectedAttackPaths.includes(path.id)}
                                onChange={() => {
                                  setSelectedAttackPaths(prev =>
                                    prev.includes(path.id)
                                      ? prev.filter(id => id !== path.id)
                                      : [...prev, path.id]
                                  );
                                }}
                                className="h-4 w-4 mt-1 text-blue-600 rounded"
                              />
                              <div className="ml-2 flex-grow">
                                <div className="flex items-center flex-wrap gap-2 mb-1">
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-bold bg-blue-100 text-blue-800 border border-blue-200 mr-2">
                                    {path.referenceCode}
                                  </span>
                                  <label htmlFor={`path-${path.id}`} className="text-sm font-medium text-gray-700 cursor-pointer">
                                    Chemin d'attaque
                                  </label>
                                  {(() => {
                                    const badge = getCountBadge(path.id);
                                    return (
                                      <span
                                        title={badge.title}
                                        className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium border ${badge.className}`}
                                      >
                                        {badge.label}
                                      </span>
                                    );
                                  })()}
                                </div>
                                <div className="flex flex-col sm:flex-row sm:items-center text-sm">
                                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800 border border-red-200">
                                    {path.sourceRiskDescription || path.sourceRiskName || 'Source inconnue'}
                                  </span>
                                  <span className="mx-1 my-1 sm:my-0 text-gray-500">→</span>
                                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800 border border-green-200">
                                    {path.objectifVise || 'Objectif non spécifié'}
                                  </span>
                                </div>
                                {path.dreadedEventName && (
                                  <div className="text-xs text-gray-500 mt-1">
                                    Événement redouté: {path.dreadedEventName}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="mt-2 flex justify-between">
                    <button
                      type="button"
                      onClick={() => setSelectedAttackPaths([])}
                      className="text-xs text-gray-500 hover:text-gray-700"
                    >
                      Désélectionner tout
                    </button>
                    <button
                      type="button"
                      onClick={() => setSelectedAttackPaths(attackPaths.map(p => p.id))}
                      className="text-xs text-blue-500 hover:text-blue-700"
                    >
                      Sélectionner tout
                    </button>
                  </div>
                </div>

                {/* Measures per attack path */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nombre de mesures par chemin d'attaque
                  </label>
                  <div className="flex items-center">
                    <input
                      type="number"
                      min="1"
                      max="10"
                      value={measuresPerPath}
                      onChange={(e) => setMeasuresPerPath(Math.max(1, Math.min(10, parseInt(e.target.value) || 1)))}
                      className="w-20 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 mr-3"
                    />
                    <span className="text-sm text-gray-500">
                      {selectedAttackPaths.length > 0 &&
                        `Total: environ ${selectedAttackPaths.length * measuresPerPath} mesures`}
                    </span>
                  </div>
                  <p className="mt-1 text-xs text-gray-500">
                    Chaque chemin d'attaque sélectionné générera ce nombre de mesures. Certaines mesures peuvent s'appliquer à plusieurs chemins d'attaque.
                  </p>
                </div>

                {/* Generation focus */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Focus de la génération
                  </label>
                  <div className="grid grid-cols-3 gap-2">
                    <button
                      type="button"
                      onClick={() => setGenerationFocus('practical')}
                      className={`px-4 py-2 border rounded-md text-sm ${generationFocus === 'practical'
                          ? 'bg-blue-50 border-blue-500 text-blue-700'
                          : 'border-gray-300 text-gray-700'
                        }`}
                    >
                      Pratique
                    </button>
                    <button
                      type="button"
                      onClick={() => setGenerationFocus('balanced')}
                      className={`px-4 py-2 border rounded-md text-sm ${generationFocus === 'balanced'
                          ? 'bg-blue-50 border-blue-500 text-blue-700'
                          : 'border-gray-300 text-gray-700'
                        }`}
                    >
                      Équilibré
                    </button>
                    <button
                      type="button"
                      onClick={() => setGenerationFocus('comprehensive')}
                      className={`px-4 py-2 border rounded-md text-sm ${generationFocus === 'comprehensive'
                          ? 'bg-blue-50 border-blue-500 text-blue-700'
                          : 'border-gray-300 text-gray-700'
                        }`}
                    >
                      Exhaustif
                    </button>
                  </div>
                  <p className="mt-1 text-xs text-gray-500">
                    {generationFocus === 'practical' && "Génère des mesures pratiques et faciles à mettre en œuvre."}
                    {generationFocus === 'balanced' && "Équilibre entre l'exhaustivité et la facilité de mise en œuvre."}
                    {generationFocus === 'comprehensive' && "Génère un ensemble complet de mesures couvrant tous les aspects de sécurité."}
                  </p>
                </div>
              </div>
            ) : (
              <div className="space-y-5">
                <p className="text-sm text-gray-700">
                  L'Assistant ACG a généré <span className="font-semibold text-green-600">{totalMeasures.length}</span> mesure{totalMeasures.length > 1 ? 's' : ''} de sécurité
                  {allGeneratedMeasures.length > 0 && generatedMeasures.length > 0 && (
                    <span className="text-gray-500"> ({allGeneratedMeasures.length} conservées + {generatedMeasures.length} nouvelles)</span>
                  )}
                  . Ajustez la sélection ci-dessous pour enchaîner une autre analyse sans perdre ce lot.
                </p>

                {/* Sélecteur conservé — compact, avec badges couvert / restant */}
                <div className="border border-blue-100 rounded-lg bg-blue-50/50 p-3">
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Chemins d'attaque
                    </label>
                    <div className="flex items-center gap-3">
                      <button
                        type="button"
                        onClick={() => setSelectedAttackPaths([])}
                        className="text-xs text-gray-500 hover:text-gray-700"
                      >
                        Désélectionner tout
                      </button>
                      <button
                        type="button"
                        onClick={() => setSelectedAttackPaths(attackPaths.map((p) => p.id))}
                        className="text-xs text-blue-500 hover:text-blue-700"
                      >
                        Sélectionner tout
                      </button>
                      <button
                        type="button"
                        onClick={selectRemainingPaths}
                        disabled={remainingPaths.length === 0}
                        className={`text-xs font-medium ${remainingPaths.length === 0
                          ? 'text-gray-400 cursor-not-allowed'
                          : 'text-purple-600 hover:text-purple-800'
                          }`}
                      >
                        Générer pour les chemins restants ({remainingPaths.length})
                      </button>
                    </div>
                  </div>
                  <div className="max-h-40 overflow-y-auto border rounded-md p-2 bg-gray-50">
                    {attackPaths.length === 0 ? (
                      <p className="text-sm text-gray-500 p-2">Aucun chemin d'attaque disponible</p>
                    ) : (
                      <div className="space-y-2">
                        {attackPaths.map((path) => {
                          const isCovered = coveredPathIds.has(path.id);
                          return (
                            <div key={path.id} className="p-2 border rounded-md bg-white hover:bg-blue-50 transition-colors">
                              <div className="flex items-start">
                                <input
                                  type="checkbox"
                                  id={`path-done-${path.id}`}
                                  checked={selectedAttackPaths.includes(path.id)}
                                  onChange={() => toggleAttackPath(path.id)}
                                  className="h-4 w-4 mt-1 text-blue-600 rounded"
                                />
                                <div className="ml-2 flex-grow">
                                  <div className="flex items-center flex-wrap gap-2 mb-1">
                                    <span className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-bold bg-blue-100 text-blue-800 border border-blue-200">
                                      {path.referenceCode}
                                    </span>
                                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium border ${isCovered
                                      ? 'bg-green-100 text-green-800 border-green-200'
                                      : 'bg-amber-100 text-amber-800 border-amber-200'
                                      }`}>
                                      {isCovered ? 'couvert' : 'restant'}
                                    </span>
                                    {(() => {
                                      const badge = getCountBadge(path.id);
                                      return (
                                        <span
                                          title={badge.title}
                                          className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium border ${badge.className}`}
                                        >
                                          {badge.label}
                                        </span>
                                      );
                                    })()}
                                  </div>
                                  <div className="flex flex-col sm:flex-row sm:items-center text-sm">
                                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800 border border-red-200">
                                      {path.sourceRiskDescription || path.sourceRiskName || 'Source inconnue'}
                                    </span>
                                    <span className="mx-1 my-1 sm:my-0 text-gray-500">→</span>
                                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800 border border-green-200">
                                      {path.objectifVise || 'Objectif non spécifié'}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>

                <div className="border rounded-md overflow-hidden">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Titre</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Catégorie</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Priorité</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Chemins d'attaque</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {totalMeasures.map((measure, index) => {
                        const isEditing = editingIndex === index;
                        const categoryKnown = CATEGORY_OPTIONS.some((o) => o.id === (isEditing ? editDraft.category : measure.category));
                        const priorityKnown = PRIORITY_OPTIONS.some((o) => o.id === (isEditing ? editDraft.priority : measure.priority));
                        return (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm text-gray-900">
                            {isEditing ? (
                              <input
                                type="text"
                                value={editDraft.title}
                                onChange={(e) => setEditDraft((p) => ({ ...p, title: e.target.value }))}
                                className="w-full rounded-md border border-gray-300 px-2 py-1 text-sm focus:border-blue-500 focus:ring-blue-500"
                              />
                            ) : (
                              measure.title
                            )}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-500">
                            {isEditing ? (
                              <select
                                value={editDraft.category}
                                onChange={(e) => setEditDraft((p) => ({ ...p, category: e.target.value }))}
                                className="w-full rounded-md border border-gray-300 px-2 py-1 text-sm focus:border-blue-500 focus:ring-blue-500"
                              >
                                {!categoryKnown && editDraft.category && (
                                  <option value={editDraft.category}>{editDraft.category}</option>
                                )}
                                {CATEGORY_OPTIONS.map((o) => (
                                  <option key={o.id} value={o.id}>{o.label}</option>
                                ))}
                              </select>
                            ) : (
                              measure.category
                            )}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-500">
                            {isEditing ? (
                              <select
                                value={editDraft.priority}
                                onChange={(e) => setEditDraft((p) => ({ ...p, priority: e.target.value }))}
                                className="w-full rounded-md border border-gray-300 px-2 py-1 text-sm focus:border-blue-500 focus:ring-blue-500"
                              >
                                {!priorityKnown && editDraft.priority && (
                                  <option value={editDraft.priority}>{editDraft.priority}</option>
                                )}
                                {PRIORITY_OPTIONS.map((o) => (
                                  <option key={o.id} value={o.id}>{o.label}</option>
                                ))}
                              </select>
                            ) : (
                              measure.priority
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex flex-wrap gap-1">
                              {measure.attackPaths && measure.attackPaths.length > 0 ? (
                                measure.attackPaths.map((pathId, idx) => {
                                  const path = attackPaths.find(p => p.id === pathId);
                                  return path ? (
                                    <span key={idx} className="inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium bg-blue-100 text-blue-800 border border-blue-200 mr-1 mb-1">
                                      {path.referenceCode}
                                    </span>
                                  ) : null;
                                })
                              ) : (
                                <span className="text-xs text-gray-500">Non spécifié</span>
                              )}

                              {/* Display source risks as additional info */}
                              {measure.sourceRisks && measure.sourceRisks.length > 0 && (
                                <div className="mt-1 text-xs text-gray-500">
                                  Sources: {measure.sourceRisks.map((source, idx) =>
                                    <span key={idx} className="inline-flex items-center px-1 py-0.5 rounded text-xs bg-red-50 text-red-700 mr-1">
                                      {typeof source === 'string' && source.length > 15 ? source.substring(0, 15) + '...' : source}
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            {isEditing ? (
                              <div className="flex items-center gap-1">
                                <button
                                  type="button"
                                  onClick={() => saveEdit(index)}
                                  title="Enregistrer"
                                  className="p-1.5 text-green-600 hover:text-green-800 hover:bg-green-50 rounded-lg transition-colors"
                                >
                                  <Check size={16} />
                                </button>
                                <button
                                  type="button"
                                  onClick={cancelEdit}
                                  title="Annuler"
                                  className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                                >
                                  <X size={16} />
                                </button>
                              </div>
                            ) : (
                              <div className="flex items-center gap-1">
                                <button
                                  type="button"
                                  onClick={() => startEdit(index)}
                                  title="Modifier"
                                  className="p-1.5 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-colors"
                                >
                                  <Pencil size={16} />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => deleteMeasure(index)}
                                  title="Supprimer"
                                  className="p-1.5 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                                >
                                  <Trash2 size={16} />
                                </button>
                              </div>
                            )}
                          </td>
                        </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="bg-gradient-to-r from-slate-50 to-slate-100 px-5 py-4 border-t border-slate-200 rounded-b-2xl">
            <div className="flex justify-between items-center">
              {totalMeasures.length === 0 ? (
                <>
                  <div className="text-sm text-gray-600">
                    {selectedAttackPaths.length > 0 && (
                      <span>
                        {selectedAttackPaths.length} chemin{selectedAttackPaths.length > 1 ? 's' : ''} d'attaque sélectionné{selectedAttackPaths.length > 1 ? 's' : ''}
                      </span>
                    )}
                  </div>
                  <div className="flex space-x-3">
                    <button
                      onClick={onClose}
                      className="px-6 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 hover:border-gray-400 transition-all duration-150"
                    >
                      Annuler
                    </button>
                    <button
                      onClick={generateMeasures}
                      disabled={isGenerating || selectedAttackPaths.length === 0}
                      className={`px-6 py-2 rounded-lg text-sm font-medium transition-all duration-150 ${isGenerating || selectedAttackPaths.length === 0
                          ? 'bg-gray-400 text-white cursor-not-allowed'
                          : 'bg-purple-600 text-white hover:bg-purple-700 shadow-sm hover:shadow-md'
                        }`}
                    >
                      {isGenerating ? (
                        <span className="flex items-center">
                          <Loader className="animate-spin mr-2" size={16} />
                          Génération...
                        </span>
                      ) : (
                        'Générer les mesures'
                      )}
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div className="text-sm text-gray-600">
                    <span className="font-medium text-green-600">{totalMeasures.length}</span> mesure{totalMeasures.length > 1 ? 's' : ''} générée{totalMeasures.length > 1 ? 's' : ''} avec succès
                    {remainingPaths.length > 0 && (
                      <span className="text-gray-500"> — {remainingPaths.length} chemin{remainingPaths.length > 1 ? 's' : ''} restant{remainingPaths.length > 1 ? 's' : ''}</span>
                    )}
                  </div>
                  <div className="flex flex-wrap justify-end gap-2">
                    <button
                      onClick={generateMeasures}
                      disabled={isGenerating || selectedAttackPaths.length === 0}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-150 ${isGenerating || selectedAttackPaths.length === 0
                        ? 'bg-gray-400 text-white cursor-not-allowed'
                        : 'bg-purple-600 text-white hover:bg-purple-700 shadow-sm hover:shadow-md'
                        }`}
                    >
                      {isGenerating ? (
                        <span className="flex items-center">
                          <Loader className="animate-spin mr-2" size={16} />
                          Génération...
                        </span>
                      ) : (
                        'Générer les mesures'
                      )}
                    </button>
                    <button
                      onClick={applyMeasures}
                      className="px-6 py-2 bg-green-600 rounded-lg text-sm font-medium text-white hover:bg-green-700 shadow-sm hover:shadow-md transition-all duration-150"
                    >
                      Appliquer ces mesures ({totalMeasures.length})
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default InlineAiSuggestions;
